import { lazy } from 'react'
import { Navigate, redirect, useLocation, useParams } from 'react-router-dom'

import { ObservableQuery, OperationVariables, WatchQueryOptions } from '@apollo/client'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Features } from 'generated/featureDefinitions'
import {
  ProjectDetailsResourceHeaderDocument,
  ProjectManagementProjectStatusEnum,
  ProjectsTabsDocument,
  SaleInvoicesDetailsPageRouteDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'
import {
  invoicesVisibility,
  quotesVisibility,
} from 'modules/project_management/lib/utils'
import { buildModuleRoute, buildNavigation } from 'modules/core/lib/react-router'

import { contactFullName } from 'modules/finance/lib/contactsUtils'
import SaleInvoiceDetailRootPage from 'modules/finance/pages/SaleInvoiceDetailRootPage'
import { SaleInvoiceHeader } from 'modules/finance/routes/ResourceHeaders/sale_invoice_header'
import { ProjectHeader } from 'modules/project_management/components/headers/ProjectHeader'
import SaleInvoiceDetailPage from 'modules/project_management/pages/ProjectDetailsPage/tabs/SaleInvoicesPage/SaleInvoiceDetailPage'
import SaleInvoicesExporterSidePanel from 'modules/project_management/pages/ProjectDetailsPage/tabs/SaleInvoicesPage/SaleInvoicesExporterSidePanel'
import SaleInvoiceUpsertPanel from 'modules/project_management/pages/ProjectDetailsPage/tabs/SaleInvoicesPage/SaleInvoicesUpsertSidePanel'
import SaleInvoiceDetailDocumentsPage from 'modules/project_management/pages/ProjectDetailsPage/tabs/SaleInvoicesPage/SalesInvoiceDocumentPage'
import ReroutingIndex from 'modules/project_management/pages/ReroutingIndex'

import { contributorsRoutes } from './contributors'
import { jobsAndRatesRoutes } from './jobsAndRates'
import { scheduleRoutes } from './schedule'

const ProjectsRootPage = lazy(() => import('modules/project_management/pages/ProjectsRootPage'))

const ImportProjectsDetailsPage = lazy(
  () => import('modules/project_management/pages/ImportProjectsPage')
)

const ImportProjectsModalPage = lazy(
  () => import('modules/project_management/pages/ImportProjectsPage/ImportProjectsModalPage')
)

const ProjectDetailsPage = lazy(() => import('modules/project_management/pages/ProjectDetailsPage'))

const ProjectEmployeesPage = lazy(
  () => import('modules/project_management/pages/ProjectDetailsPage/Employees')
)

const AssignEmployeesSingleProjectModalPage = lazy(
  () =>
    import('modules/project_management/pages/ProjectDetailsPage/Employees/AssignEmployeesSingleProjectModalPage')
)

const AssignSingleEmployeeModalPage = lazy(
  () =>
    import('modules/project_management/pages/ProjectDetailsPage/Employees/AssignSingleEmployeeModalPage')
)

const UnassignSingleEmployeeModalPage = lazy(
  () =>
    import('modules/project_management/pages/ProjectDetailsPage/Employees/UnassignSingleEmployeeModalPage')
)

const ExportProjectDetailsModalPage = lazy(
  () => import('modules/project_management/pages/ProjectDetailsPage/ExportProjectDetailsModalPage')
)

const OverviewPage = lazy(
  () => import('modules/project_management/pages/ProjectDetailsPage/tabs/OverviewPage')
)

const SettingsPage = lazy(
  () => import('modules/project_management/pages/ProjectDetailsPage/SettingsPage')
)

const SpendingPage = lazy(
  () => import('modules/project_management/pages/ProjectDetailsPage/tabs/SpendingPage')
)

const SubprojectsPage = lazy(
  () => import('modules/project_management/pages/ProjectDetailsPage/tabs/SubprojectsPage')
)

const AddSubprojectModalPage = lazy(
  () =>
    import('modules/project_management/pages/ProjectDetailsPage/tabs/SubprojectsPage/AddSubprojectModalPage')
)

const DeleteSubprojectModalPage = lazy(
  () =>
    import('modules/project_management/pages/ProjectDetailsPage/tabs/SubprojectsPage/DeleteSubprojectModalPage')
)

const EditSubprojectModalPage = lazy(
  () =>
    import('modules/project_management/pages/ProjectDetailsPage/tabs/SubprojectsPage/EditSubprojectModalPage')
)

const ProjectTasksPage = lazy(
  () => import('modules/project_management/pages/ProjectDetailsPage/TasksPage')
)

const TaskDetail = lazy(
  () => import('modules/project_management/pages/ProjectDetailsPage/TasksPage/TaskDetail')
)

const ProjectsDefaultPage = lazy(
  () => import('modules/project_management/pages/ProjectsDefaultPage')
)
const ProjectsListPage = lazy(() => import('modules/project_management/pages/ProjectsListPage'))

const ActivateProjectModalPage = lazy(
  () => import('modules/project_management/pages/ProjectsListPage/ActivateProjectModalPage')
)

const CloseProjectModalPage = lazy(
  () => import('modules/project_management/pages/ProjectsListPage/CloseProjectModalPage')
)

const DuplicateProjectModalPage = lazy(
  () => import('modules/project_management/pages/ProjectsListPage/DuplicateProjectModalPage')
)

const ExportProjectsModalPage = lazy(
  () => import('modules/project_management/pages/ProjectsListPage/ExportProjectsModalPage')
)

const SoftDeleteProjectModalPage = lazy(
  () => import('modules/project_management/pages/ProjectsListPage/SoftDeleteProjectModalPage')
)

const ProjectsOnboardingPage = lazy(
  () => import('modules/project_management/pages/ProjectsOnboardingPage')
)

const ProjectSaleInvoicesPage = lazy(
  () => import('modules/project_management/pages/ProjectDetailsPage/tabs/SaleInvoicesPage')
)

const ProjectQuotesPage = lazy(
  () => import('modules/project_management/pages/ProjectDetailsPage/tabs/ProjectQuotesPage')
)

const DocumentsPage = lazy(
  () => import('modules/project_management/pages/ProjectDetailsPage/tabs/DocumentsPage')
)

type enumOrigins = 'projectList' | 'projectSettings'

interface routesProps {
  projectId: string
  watchQuery: (
    options: WatchQueryOptions<OperationVariables>
  ) => ObservableQuery<OperationVariables>
  origin: enumOrigins
  session: { company: { id: string } }
}

interface ProjectRedirectComponentProps {
  navigation: Navigation<NavigationPaths>
}

const ProjectOverviewRedirectComponent = ({ navigation }: ProjectRedirectComponentProps) => {
  const { id } = useParams()
  const location = useLocation()

  return (
    <Navigate
      to={navigation.projects.id.details.overview.fullPath({ id: id ? Number(id) : 0 })}
      state={location.state}
      replace
    />
  )
}

export const projectManagementRoutes = buildNavigation((navigation) => {
  const routeLogic = ({ projectId, watchQuery, origin, session }: routesProps) => {
    return watchQuery({
      query: ProjectDetailsResourceHeaderDocument,
      variables: {
        projectId: projectId,
        companyId: session.company.id,
        featureIds: [
          Features.projects_tasks,
          Features.projects_costs,
          Features.projects_budget,
          Features.sales_invoices_management,
          Features.projects_overview,
          Features.sales_quotes_management,
        ],
      },
    }).map(({ data }) => {
      const project = data?.projectManagement.project
      const canRequestUpsell = data?.upsell?._policies?.canRequestUpsell?.can ?? false
      if (!project) {
        return { title: '' }
      }

      const hasQuickQuoteBuilderFF = !!data?.features.featuresConnection.nodes.find(
        (feature: { name: string }) => feature.name === 'dev_quick_quote_builder'
      )?.enabled

      const projectStatus: ProjectManagementProjectStatusEnum = project.status
      const canManageProject = project?._policies?.assignEditor.can ?? false
      // assignOwner policy checks if the user is an admin or an owner of the project. We should rename it to isAdminOrOwner.
      const isOwnerOrAdmin = project?._policies?.assignOwner.can ?? false
      const isSpanishCompany =
        data?.companies.legalEntitiesConnection.nodes.find(
          (legalEntity: { main: boolean; country: string }) => legalEntity.main
        )?.country === 'es' // Harcoding this country cause we know we dont sell invoices in other countries so upsell wouldnt make sense

      const canSeeLaborCost = project?._policies?.seeLaborCost.can ?? false

      const hasProjectTasks = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
        (feature: { id: string }) => feature.id === Features.projects_tasks
      )?.available

      const hasProjectsCostsFeature = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
        (feature: { id: string }) => feature.id === Features.projects_costs
      )?.available

      const hasProjectBudget = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
        (feature: { id: string }) => feature.id === Features.projects_budget
      )?.available

      const hasSalesInvoicesManagementFeature =
        !!data?.companies.featureAvailabilitiesConnection.nodes.find(
          (feature: { id: string }) => feature.id === Features.sales_invoices_management
        )?.available

      const hasSalesQuotesManagementFeature =
        !!data?.companies.featureAvailabilitiesConnection.nodes.find(
          (feature: { id: string }) => feature.id === Features.sales_quotes_management
        )?.available

      const { canSeeSalesInvoicesPageContent, canSeeSalesInvoicesUpsell } =
        invoicesVisibility(
          hasProjectsCostsFeature,
          canRequestUpsell,
          isOwnerOrAdmin,
          isSpanishCompany,
          hasSalesInvoicesManagementFeature
        )
      const { canSeeSalesQuotesPageContent, canSeeSalesQuotesUpsell } =
        quotesVisibility(
          hasProjectsCostsFeature,
          canRequestUpsell,
          isOwnerOrAdmin,
          hasSalesQuotesManagementFeature,
          hasQuickQuoteBuilderFF
        )

      const canSeeSalesInvoicesPage = canSeeSalesInvoicesPageContent || canSeeSalesInvoicesUpsell
      const canSeeSalesQuotesPage = canSeeSalesQuotesPageContent || canSeeSalesQuotesUpsell

      const hasDocuments = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
        (feature: { id: string }) => feature.id === Features.projects_overview
      )?.available

      const canSeeRates = hasProjectBudget && hasProjectsCostsFeature && canSeeLaborCost

      const peopleTabTitle = canSeeRates
        ? i18n.t('project_management.people_and_rates.people_tab.with_rates')
        : i18n.t('project_management.people_and_rates.people_tab.title')

      if (origin == 'projectSettings')
        return {
          title: i18n.t('project_management.meta_data.project_settings_page.title'),
          headerComponent: (
            <ResourceHeader
              title={i18n.t('project_management.meta_data.project_settings_page.title')}
            />
          ),
        }
      else
        return {
          title: i18n.t('project_management.meta_data.project_page.title', {
            project_name: project.name,
          }),
          headerComponent: (
            <ProjectHeader
              status={projectStatus}
              projectName={project.name}
              startDate={project.startDate}
              dueDate={project.dueDate}
              code={project.code}
              projectId={project.id}
              client={contactFullName(project.client)}
              canManageProject={canManageProject}
            />
          ),
          subNavigation: [
            {
              id: 'navigation.projects.id.details.overview',
              href: navigation.projects.id.details.overview.fullPath({ id: Number(project.id) }),
              title: i18n.t('project_management.overview.overview_tab'),
              index: true,
            },
            {
              id: 'navigation.projects.id.details.subprojects',
              href: navigation.projects.id.details.subprojects.fullPath({ id: Number(project.id) }),
              title: i18n.t('project_management.subprojects.subprojects_tab'),
            },
            {
              id: 'navigation.projects.id.details.tasks',
              href: navigation.projects.id.details.tasks.fullPath({ id: Number(project.id) }),
              title: i18n.t('project_management.tasks.tasks_tab'),
              variant: !hasProjectTasks ? ('upsell' as const) : ('default' as const),
            },
            {
              id: 'navigation.projects.id.details.employees',
              href: navigation.projects.id.details.employees.fullPath({ id: Number(project.id) }),
              title: peopleTabTitle,
            },
            {
              id: 'navigation.projects.id.details.spending',
              href: navigation.projects.id.details.spending.fullPath({ id: Number(project.id) }),
              title: i18n.t('project_management.spending.spending_tab'),
              isVisible: project?._policies?.assignOwner.can ?? false,
              variant: !hasProjectsCostsFeature ? ('upsell' as const) : ('default' as const),
            },
            {
              id: 'navigation.projects.id.details.sale_invoices',
              href: navigation.projects.id.details.sale_invoices.fullPath({
                id: Number(project.id),
              }),
              title: i18n.t('project_management.sale_invoices.tabs.sales'),
              isVisible: canSeeSalesInvoicesPage || canSeeSalesQuotesPage,
              variant:
                canSeeSalesInvoicesUpsell || canSeeSalesQuotesUpsell
                  ? ('upsell' as const)
                  : ('default' as const),
            },
            {
              id: 'navigation.projects.id.details.documents',
              href: navigation.projects.id.details.documents.fullPath({ id: Number(project.id) }),
              title: i18n.t('project_management.files.tab'),
              variant: !hasDocuments ? ('upsell' as const) : ('default' as const),
            },
            {
              id: 'navigation.projects.id.settings',
              href: navigation.projects.id.details.settings.fullPath({ id: Number(project.id) }),
              title: i18n.t('common.settings'),
              isVisible: canManageProject,
            },
          ],
        }
    })
  }

  return [
    buildModuleRoute({
      path: navigation.projects.path,
      element: <ProjectsRootPage />,
      handle: {
        module: 'project_management',
        moduleName: () => i18n.t('sidebar.projects_management'),
        meta: ({ watchQuery }) => {
          return watchQuery({
            query: ProjectsTabsDocument,
          }).map(({ data }) => {
            const canSeeContributorsPage =
              !!data?.projectManagement._policies?.pagesSeeContributorsList.can
            const canSeeContributorsPageUpsell =
              !!data?.projectManagement._policies?.pagesSeeContributorsListUpsell.can

            const canSeeSchedulePage =
              data?.projectManagement._policies?.pagesSeeScheduleContributors.can
            const canSeeSchedulePageUpsell =
              data?.projectManagement._policies?.pagesSeeScheduleContributorsUpsell.can

            const canSeeJobsAndRatesPage =
              !!data?.projectManagement._policies?.pagesSeeJobsAndRates.can
            const canSeeJobsAndRatesUpsell =
              !!data?.projectManagement._policies?.pagesSeeJobsAndRatesUpsell.can

            return {
              title: i18n.t('project_management.meta_data.projects_list_page.title'),
              description: i18n.t('project_management.meta_data.projects_list_page.description'),
              illustration: 'tasks_management',
              subNavigation: [
                {
                  id: 'navigation.projects.list.path',
                  title: i18n.t('project_management.meta_data.projects_list_page.title'),
                  href: navigation.projects.list.fullPath(),
                  index: true,
                },
                {
                  id: 'navigation.projects.schedule.path',
                  title: i18n.t('project_management.meta_data.schedule.title'),
                  isVisible: canSeeSchedulePage || canSeeSchedulePageUpsell,
                  variant: canSeeSchedulePageUpsell ? ('upsell' as const) : ('default' as const),
                  href: navigation.projects.schedule.fullPath(),
                },
                {
                  id: 'navigation.projects.jobsAndRates.path',
                  title: i18n.t('project_management.meta_data.jobs_and_rates.title'),
                  href: navigation.projects.jobsAndRates.fullPath(),
                  isVisible: canSeeJobsAndRatesPage || canSeeJobsAndRatesUpsell,
                  variant: canSeeJobsAndRatesUpsell ? ('upsell' as const) : ('default' as const),
                },
                {
                  id: 'navigation.projects.contributors.path',
                  title: i18n.t('project_management.meta_data.contributors.title'),
                  href: navigation.projects.contributors.fullPath(),
                  isVisible: canSeeContributorsPage || canSeeContributorsPageUpsell,
                  variant: canSeeContributorsPageUpsell
                    ? ('upsell' as const)
                    : ('default' as const),
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <ProjectsDefaultPage />,
        },
        {
          path: navigation.projects.onboarding.path,
          element: <ProjectsOnboardingPage />,
        },
        {
          path: navigation.projects.importDetails.path,
          handle: {
            meta: () => {
              return {
                title: i18n.t('project_management.imports.title'),
              }
            },
          },
          element: <ImportProjectsDetailsPage />,
        },
        {
          path: navigation.projects.list.path,
          children: [
            {
              path: navigation.projects.list.export.path,
              element: <ExportProjectsModalPage />,
            },
            {
              path: navigation.projects.list.import.path,
              element: <ImportProjectsModalPage />,
            },
            {
              path: navigation.projects.list.projectId.path,
              children: [
                {
                  path: navigation.projects.list.projectId.duplicate.path,
                  element: <DuplicateProjectModalPage />,
                },
                {
                  path: navigation.projects.list.projectId.close.path,
                  element: <CloseProjectModalPage />,
                },
                {
                  path: navigation.projects.list.projectId.reopen.path,
                  element: <ActivateProjectModalPage />,
                },
                {
                  path: navigation.projects.list.projectId.delete.path,
                  element: <SoftDeleteProjectModalPage />,
                },
              ],
            },
          ],
          element: <ProjectsListPage />,
        },
        ...contributorsRoutes(navigation),
        ...scheduleRoutes(navigation),
        ...jobsAndRatesRoutes(navigation),
        {
          path: navigation.projects.id.path,
          handle: {
            resource: true,
            meta: ({ params, watchQuery, session }) => {
              const projectId = params['id']
              if (!projectId) return { title: '' }

              return routeLogic({
                projectId,
                watchQuery,
                origin: 'projectList',
                session,
              })
            },
          },
          children: [
            {
              index: true,
              element: <ProjectOverviewRedirectComponent navigation={navigation} />,
            },
            {
              path: navigation.projects.id.details.path,
              children: [
                {
                  path: navigation.projects.id.details.overview.path,

                  children: [
                    {
                      path: navigation.projects.id.details.overview.export.path,
                      element: <ExportProjectDetailsModalPage />,
                    },
                  ],
                  element: <OverviewPage />,
                },
                {
                  path: navigation.projects.id.details.subprojects.path,
                  children: [
                    {
                      path: navigation.projects.id.details.subprojects.create.path,
                      element: <AddSubprojectModalPage />,
                    },
                    {
                      path: navigation.projects.id.details.subprojects.export.path,
                      element: <ExportProjectDetailsModalPage />,
                    },
                    {
                      path: navigation.projects.id.details.subprojects.subprojectId.path,
                      children: [
                        {
                          path: navigation.projects.id.details.subprojects.subprojectId.edit.path,
                          element: <EditSubprojectModalPage />,
                        },
                        {
                          path: navigation.projects.id.details.subprojects.subprojectId.delete.path,
                          element: <DeleteSubprojectModalPage />,
                        },
                      ],
                    },
                  ],
                  element: <SubprojectsPage />,
                },
                {
                  path: navigation.projects.id.details.spending.path,
                  element: <SpendingPage />,
                },
                {
                  path: navigation.projects.id.details.tasks.path,
                  children: [
                    {
                      path: navigation.projects.id.details.tasks.export.path,
                      element: <ExportProjectDetailsModalPage />,
                    },
                    {
                      path: navigation.projects.id.details.tasks.taskId.path,
                      element: <TaskDetail />,
                    },
                  ],
                  element: <ProjectTasksPage />,
                },
                {
                  path: navigation.projects.id.details.employees.path,
                  children: [
                    {
                      path: navigation.projects.id.details.employees.export.path,
                      element: <ExportProjectDetailsModalPage />,
                    },
                    {
                      path: navigation.projects.id.details.employees.workers.path,
                      element: <AssignEmployeesSingleProjectModalPage />,
                    },
                    {
                      path: navigation.projects.id.details.employees.unassignEmployee.path,
                      children: [
                        {
                          path: navigation.projects.id.details.employees.unassignEmployee.employeeId
                            .path,
                          children: [
                            {
                              path: navigation.projects.id.details.employees.unassignEmployee
                                .employeeId.projectWorkerId.path,
                              element: <UnassignSingleEmployeeModalPage />,
                            },
                          ],
                        },
                      ],
                    },
                    {
                      path: navigation.projects.id.details.employees.assignEmployee.path,
                      children: [
                        {
                          path: navigation.projects.id.details.employees.assignEmployee.employeeId
                            .path,
                          children: [
                            {
                              path: navigation.projects.id.details.employees.assignEmployee
                                .employeeId.projectWorkerId.path,
                              element: <AssignSingleEmployeeModalPage />,
                            },
                          ],
                        },
                      ],
                    },
                  ],
                  element: <ProjectEmployeesPage />,
                },
                {
                  path: navigation.projects.id.details.sale_invoices.path,
                  handle: {
                    meta: ({ params, watchQuery, session }) => {
                      const projectId = params['id']
                      if (!projectId) return { title: '' }

                      return watchQuery({
                        query: ProjectDetailsResourceHeaderDocument,
                        variables: {
                          projectId: projectId,
                          companyId: session.company.id,
                          featureIds: [
                            Features.projects_costs,
                            Features.sales_invoices_management,
                            Features.sales_quotes_management,
                          ],
                        },
                      }).map(({ data }) => {
                        const project = data?.projectManagement.project
                        if (!project) return { title: '' }

                        const canRequestUpsell =
                          data?.upsell?._policies?.canRequestUpsell?.can ?? false
                        const isOwnerOrAdmin = project?._policies?.assignOwner.can ?? false
                        const hasProjectsCostsFeature =
                          !!data?.companies.featureAvailabilitiesConnection.nodes.find(
                            (feature: { id: string }) => feature.id === Features.projects_costs
                          )?.available
                        const hasSalesInvoicesManagementFeature =
                          !!data?.companies.featureAvailabilitiesConnection.nodes.find(
                            (feature: { id: string }) =>
                              feature.id === Features.sales_invoices_management
                          )?.available
                        const hasSalesQuotesManagementFeature =
                          !!data?.companies.featureAvailabilitiesConnection.nodes.find(
                            (feature: { id: string }) =>
                              feature.id === Features.sales_quotes_management
                          )?.available
                        const hasQuickQuoteBuilderFF =
                          !!data?.features.featuresConnection.nodes.find(
                            (feature: { name: string }) => feature.name === 'dev_quick_quote_builder'
                          )?.enabled

                        const isSpanishCompany =
                          data?.companies.legalEntitiesConnection?.nodes.find(
                            (le: { main: boolean }) => le.main
                          )?.country === 'es'

                          const {
                            canSeeSalesInvoicesPageContent,
                            canSeeSalesInvoicesUpsell,
                          } = invoicesVisibility(
                            hasProjectsCostsFeature,
                            canRequestUpsell,
                            isOwnerOrAdmin,
                            isSpanishCompany,
                            hasSalesInvoicesManagementFeature
                          )
                          const {
                            canSeeSalesQuotesPageContent,
                            canSeeSalesQuotesUpsell,
                          } = quotesVisibility(
                            hasProjectsCostsFeature,
                            canRequestUpsell,
                            isOwnerOrAdmin,
                            hasSalesQuotesManagementFeature,
                            hasQuickQuoteBuilderFF
                          )

                        const canSeeSalesQuotesPage =
                          canSeeSalesQuotesPageContent || canSeeSalesQuotesUpsell

                        const canSeeSalesInvoicesPage =
                          canSeeSalesInvoicesPageContent || canSeeSalesInvoicesUpsell

                        return {
                          subNavigation: [
                            {
                              id: 'navigation.projects.id.details.sale_invoices.invoices',
                              href: navigation.projects.id.details.sale_invoices.invoices.fullPath({
                                id: Number(project.id),
                              }),
                              title: i18n.t('project_management.sale_invoices.tabs.invoices'),
                              isVisible: canSeeSalesInvoicesPage,
                              variant: canSeeSalesInvoicesUpsell
                                ? ('upsell' as const)
                                : ('default' as const),
                              index: true,
                            },
                            {
                              id: 'navigation.projects.id.details.sale_invoices.quotes',
                              href: navigation.projects.id.details.sale_invoices.quotes.fullPath({
                                id: Number(project.id),
                              }),
                              title: i18n.t('project_management.sale_invoices.tabs.quotes'),
                              isVisible: canSeeSalesQuotesPage,
                              variant: canSeeSalesQuotesUpsell
                                ? ('upsell' as const)
                                : ('default' as const),
                            },
                          ],
                        }
                      })
                    },
                  },
                  children: [
                    {
                      index: true,
                      element: <ReroutingIndex />,
                    },
                    {
                      path: navigation.projects.id.details.sale_invoices.invoices.path,
                      children: [
                        {
                          path: navigation.projects.id.details.sale_invoices.invoices.create.path,
                          element: <SaleInvoiceUpsertPanel />,
                        },
                        {
                          path: navigation.projects.id.details.sale_invoices.invoices.invoiceId
                            .path,
                          element: <SaleInvoiceUpsertPanel />,
                        },
                        {
                          path: navigation.projects.id.details.sale_invoices.invoices.export.path,
                          element: <SaleInvoicesExporterSidePanel />,
                        },
                        {
                          path: navigation.projects.id.details.sale_invoices.invoices.detail.path,
                          element: <SaleInvoiceDetailRootPage />,
                          handle: {
                            resource: true,
                            meta: ({ params, watchQuery }) =>
                              watchQuery({
                                query: SaleInvoicesDetailsPageRouteDocument,
                                variables: {
                                  id: params['invoiceId'] ?? '',
                                },
                              }).map(({ data }) => {
                                const saleInvoice = data?.finance.financialDocument
                                const saleInvoiceTitle =
                                  saleInvoice && saleInvoice.documentNumber
                                    ? saleInvoice.documentNumber
                                    : '-'

                                return {
                                  title: saleInvoiceTitle,
                                  headerComponent: (
                                    <SaleInvoiceHeader
                                      saleInvoice={saleInvoice}
                                      projectId={params.id}
                                    />
                                  ),
                                  subNavigation: saleInvoice
                                    ? [
                                        {
                                          id: 'overview',
                                          title: i18n.t('finance.sale_invoices.detail.overview'),
                                          href: navigation.projects.id.details.sale_invoices.invoices.detail.overview.fullPath(
                                            {
                                              id: Number(params.id),
                                              invoiceId: saleInvoice.id,
                                            }
                                          ),
                                          isVisible: true,
                                        },
                                        {
                                          id: 'documents',
                                          title: i18n.t('finance.sale_invoices.detail.documents'),
                                          href: navigation.projects.id.details.sale_invoices.invoices.detail.documents.fullPath(
                                            {
                                              id: Number(params.id),
                                              invoiceId: saleInvoice.id,
                                            }
                                          ),
                                          isVisible: true,
                                        },
                                      ]
                                    : undefined,
                                }
                              }),
                          },
                          children: [
                            {
                              index: true,
                              loader: ({ params }) => {
                                return redirect(
                                  navigation.projects.id.details.sale_invoices.invoices.detail.overview.fullPath(
                                    {
                                      id: Number(params.id),
                                      invoiceId: Number(params.invoiceId),
                                    }
                                  )
                                )
                              },
                            },
                            {
                              path: navigation.projects.id.details.sale_invoices.invoices.detail
                                .overview.path,
                              element: <SaleInvoiceDetailPage />,
                            },
                            {
                              path: navigation.projects.id.details.sale_invoices.invoices.detail
                                .documents.path,
                              element: <SaleInvoiceDetailDocumentsPage />,
                            },
                            {
                              path: navigation.projects.id.details.sale_invoices.invoices.detail
                                .form.path,
                              element: <SaleInvoiceUpsertPanel />,
                            },
                          ],
                        },
                      ],
                      element: <ProjectSaleInvoicesPage />,
                    },
                    {
                      path: navigation.projects.id.details.sale_invoices.quotes.path,
                      element: <ProjectQuotesPage />,
                    },
                  ],
                },
                {
                  path: navigation.projects.id.details.documents.path,
                  element: <DocumentsPage />,
                },
                {
                  path: navigation.projects.id.details.settings.path,
                  handle: {
                    meta: ({ params, watchQuery, session }) => {
                      const projectId = params['id']
                      if (!projectId) return { title: '' }

                      return routeLogic({
                        projectId,
                        watchQuery,
                        origin: 'projectSettings',
                        session,
                      })
                    },
                  },
                  element: <SettingsPage />,
                },
              ],
              element: <ProjectDetailsPage />,
            },
          ],
        },
      ],
    }),
  ]
})
